import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { scrollRevealIntoView, lockRevealScroll, scheduleRevealScroll, markTyping } from "./reveal";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Checkbox } from "../ui/checkbox";
import { Mic, MicOff } from "lucide-react";
import { useDictation, sttSupported } from "./useSpeech";

const slug = (s) =>
  String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 40);

export const SectionTitle = ({ children, className = "" }) => (
  <h3 className={`font-serif text-2xl text-brand-black ${className}`}>{children}</h3>
);

export const SectionIntro = ({ children }) => (
  <p className="text-sm text-brand-darkgrey leading-relaxed">{children}</p>
);

export const Field = ({ label, hint, optional, required, children, testid, num, show, scroll = true }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (show !== true || !scroll) return undefined;
    const id = setTimeout(() => scheduleRevealScroll(ref.current), 130);
    return () => clearTimeout(id);
  }, [show, scroll]);
  if (show === false) return null;
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, ease: "easeOut" }}
      className="space-y-2"
      data-testid={testid}
    >
      {label && (
        <Label className="text-sm font-semibold text-brand-black block leading-snug">
          {num != null && <span className="text-brand-gold mr-1.5">{num}.</span>}
          {label}
          {required && <span className="text-brand-danger font-normal"> *</span>}
          {optional && <span className="text-muted-foreground font-normal"> (optional)</span>}
        </Label>
      )}
      {hint && <p className="text-xs text-muted-foreground -mt-1">{hint}</p>}
      {children}
    </motion.div>
  );
};

export const TextField = ({ value, onChange, placeholder, testid, type = "text", disabled }) => (
  <Input
    type={type}
    value={value || ""}
    disabled={disabled}
    onChange={(e) => { markTyping(); onChange(e.target.value); }}
    placeholder={placeholder}
    data-testid={testid}
    className="bg-white border-input focus-visible:ring-brand-gold rounded-sm"
  />
);

export const NumberField = ({ value, onChange, placeholder, testid, disabled }) => (
  <Input
    type="number"
    inputMode="numeric"
    min="0"
    value={value ?? ""}
    disabled={disabled}
    onChange={(e) => { markTyping(); onChange(e.target.value.replace(/[^0-9]/g, "")); }}
    placeholder={placeholder}
    data-testid={testid}
    className="bg-white border-input focus-visible:ring-brand-gold rounded-sm"
  />
);

export const LongText = ({ value, onChange, placeholder, testid, rows = 4, disabled }) => {
  const valueRef = useRef(value);
  valueRef.current = value;
  const { listening, start, stop } = useDictation((t) => {
    markTyping();
    const cur = valueRef.current || "";
    onChange(cur ? `${cur.replace(/\s+$/, "")} ${t}` : t);
  });
  return (
    <div className="relative">
      <Textarea
        value={value || ""}
        rows={rows}
        disabled={disabled}
        onChange={(e) => { markTyping(); onChange(e.target.value); }}
        placeholder={listening ? "Listening… speak now" : placeholder}
        data-testid={testid}
        className={`bg-white border-input focus-visible:ring-brand-gold rounded-sm ${sttSupported ? "pr-12" : ""}`}
      />
      {sttSupported && !disabled && (
        <button type="button" onClick={listening ? stop : start} data-testid={testid ? `${testid}-mic` : undefined}
          aria-label={listening ? "Stop listening" : "Speak your answer"} aria-pressed={listening}
          title={listening ? "Stop listening" : "Speak your answer"}
          className={`absolute right-2 bottom-2 h-8 w-8 rounded-full inline-flex items-center justify-center border transition-colors ${listening ? "bg-brand-danger text-white border-brand-danger animate-pulse" : "bg-white border-border text-brand-darkgrey hover:border-brand-gold hover:text-brand-gold"}`}>
          {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
        </button>
      )}
    </div>
  );
};

export const RadioField = ({ options, value, onChange, testid, name = "radio", after = {} }) => (
  <RadioGroup value={value || ""} onValueChange={onChange} className="space-y-2" data-testid={testid}>
    {options.map((opt) => {
      const val = typeof opt === "string" ? opt : opt.value;
      const label = typeof opt === "string" ? opt : opt.label;
      const id = `${name}-${slug(val)}`;
      return (
        <React.Fragment key={val}>
          <label
            htmlFor={id}
            className="flex items-start gap-3 border border-border bg-white p-3 cursor-pointer hover:border-brand-gold transition-colors duration-200"
          >
            <RadioGroupItem
              value={val}
              id={id}
              data-testid={`${testid}-${slug(val)}`}
              className="mt-0.5 border-brand-darkgrey text-brand-gold"
            />
            <span className="text-sm text-brand-black leading-snug">{label}</span>
          </label>
          {after[val] || null}
        </React.Fragment>
      );
    })}
  </RadioGroup>
);

export const CheckboxGroupField = ({ options, value = [], onChange, exclusive = [], testid, extras = {} }) => {
  const toggle = (val) => {
    lockRevealScroll(); // multi-select: don't scroll away while the user keeps choosing
    let next;
    if (exclusive.includes(val)) {
      next = value.includes(val) ? [] : [val];
    } else {
      const base = value.filter((v) => !exclusive.includes(v));
      next = base.includes(val) ? base.filter((v) => v !== val) : [...base, val];
    }
    onChange(next);
  };
  return (
    <div className="space-y-2" data-testid={testid}>
      {options.map((opt) => {
        const val = typeof opt === "string" ? opt : opt.value;
        const label = typeof opt === "string" ? opt : opt.label;
        const id = `${testid}-${slug(val)}`;
        const checked = value.includes(val);
        return (
          <React.Fragment key={val}>
            <label
              htmlFor={id}
              className="flex items-start gap-3 border border-border bg-white p-3 cursor-pointer hover:border-brand-gold transition-colors duration-200"
            >
              <Checkbox
                id={id}
                checked={checked}
                onCheckedChange={() => toggle(val)}
                data-testid={id}
                className="mt-0.5 border-brand-darkgrey data-[state=checked]:bg-brand-gold data-[state=checked]:border-brand-gold"
              />
              <span className="text-sm text-brand-black leading-snug">{label}</span>
            </label>
            {extras[val] ? <div className="pl-8">{extras[val]}</div> : null}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export const InlineCheck = ({ checked, onChange, label, testid, disabled }) => (
  <label className={`flex items-center gap-2 text-sm text-brand-darkgrey mt-2 ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
    <Checkbox
      checked={!!checked}
      onCheckedChange={onChange}
      disabled={disabled}
      data-testid={testid}
      className="border-brand-darkgrey data-[state=checked]:bg-brand-gold data-[state=checked]:border-brand-gold"
    />
    {label}
  </label>
);

export const InfoPanel = ({ children, tone = "gold", testid }) => {
  const tones = {
    gold: "border-brand-gold bg-accent/60 text-brand-black",
    danger: "border-brand-danger bg-red-50 text-brand-black",
    grey: "border-border bg-brand-grey text-brand-darkgrey",
  };
  return (
    <div className={`border-l-4 p-4 text-sm leading-relaxed ${tones[tone]}`} data-testid={testid}>
      {children}
    </div>
  );
};
